import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { Truck, Package, DollarSign, Gift, Zap, Clock, MapPin } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import type { ShippingRateFormData } from '../types/shipping-form.types';
import {
  SHIPPING_RATE_TYPES,
  SHIPPING_DELIVERY_TYPES,
  SHIPPING_CURRENCIES,
} from '../types/shipping-form.types';
import type { ShippingRateType, DeliveryType } from '../types/shipping.types';

interface ShippingRateTypeSelectorProps {
  form: UseFormReturn<ShippingRateFormData>;
}

const RATE_TYPE_ICONS: Record<ShippingRateType, typeof Truck> = {
  flat: Truck,
  weight_based: Package,
  price_based: DollarSign,
  free_shipping: Gift,
};

const DELIVERY_TYPE_ICONS: Record<DeliveryType, typeof Truck> = {
  standard: Truck,
  express: Zap,
  same_day: Clock,
  pickup: MapPin,
};

export function ShippingRateTypeSelector({ form }: ShippingRateTypeSelectorProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shipping.rateType', { defaultValue: 'Rate Type' })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="rateType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shipping.calculationType', { defaultValue: 'Calculation Type' })} *</FormLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SHIPPING_RATE_TYPES.map((type) => {
                  const Icon = RATE_TYPE_ICONS[type.value];
                  const isSelected = field.value === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => field.onChange(type.value)}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <Icon className={`size-5 mt-0.5 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="min-w-0">
                        <p className="font-medium">{t(type.labelKey, { defaultValue: type.value })}</p>
                        <p className="text-xs text-muted-foreground">
                          {t(type.description, { defaultValue: '' })}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="deliveryType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shipping.deliveryMethod', { defaultValue: 'Delivery Method' })} *</FormLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SHIPPING_DELIVERY_TYPES.map((type) => {
                  const Icon = DELIVERY_TYPE_ICONS[type.value];
                  const isSelected = field.value === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => field.onChange(type.value)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <Icon className={`size-4 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium truncate">{t(type.labelKey, { defaultValue: type.value })}</span>
                    </button>
                  );
                })}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shipping.currency', { defaultValue: 'Currency' })} *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SHIPPING_CURRENCIES.map((currency) => (
                    <SelectItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
